import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../shared/abstractStore';
import { meDashboardViewModelQuery, meDashboardViewModelQueryVariables } from '../generated/meDashboardViewModelQuery';
import { mainApiConfig } from '../../../configure/mainApiConfig';
import { assessmentFieldsFragment } from '../models/Assessment';
import { userAssessmentAssignmentFieldsFragment } from '../models/UserAssessmentAssignment';
import { assessmentSessionFieldsFragment } from '../models/AssessmentSession';
import { profileFieldsFragment } from '../models/Profile';
import { subscriptionAssessmentFieldsFragment } from '../models/SubscriptionAssessment';
import { subscriptionFieldsFragment } from '../models/Subscription';

export interface MeDashboardViewModelOptions extends AsyncLoadOptions {
    isUserId?: boolean,
}


/**
 * Get a Profile from the store.
 * @param id
 * @param options
 */
export function useMeDashboardViewModel(id: string | undefined | null, options: MeDashboardViewModelOptions = {}): AsyncLoadResult<meDashboardViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<meDashboardViewModelQuery, meDashboardViewModelQueryVariables>(
        gql`
        query meDashboardViewModelQuery ($profileId: ID, $userId: ID) {
            viewModel: meDashboardViewModel (profileId: $profileId, userId: $userId) {
                id
                assessments {
                    ...assessmentFields
                }
                userAssessmentAssignments {
                    ...userAssessmentAssignmentFields
                }
                assessmentSessions {
                    ...assessmentSessionFields
                }
                subscriptionAssessments {
                    ...subscriptionAssessmentFields
                }

                subscription {
                    ...subscriptionFields
                }
            }

            profile (id: $profileId, userId: $userId) {
                ...profileFields
            }
        }

        ${assessmentFieldsFragment}
        ${userAssessmentAssignmentFieldsFragment}
        ${assessmentSessionFieldsFragment}
        ${profileFieldsFragment}
        ${subscriptionAssessmentFieldsFragment}
        ${subscriptionFieldsFragment}
        `,
        {
            variables: {
                profileId: !options.isUserId ? id ?? Guid.empty : null,
                userId: options.isUserId ? id ?? Guid.empty : null,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
